import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);
// Si estamos en un dominio distinto al configurado (ej: custom domain ceningenieria.com),
// usa el origen actual para que las llamadas /api/* sean same-origin y no tengan problemas CORS
const _configuredUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const _configuredOrigin = new URL(_configuredUrl).origin;
const _currentOrigin = window.location.origin;
const BACKEND_URL = _configuredOrigin === _currentOrigin ? _configuredUrl : _currentOrigin;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for token refresh
axios.interceptors.response.use(
  (res) => res,
  async (error) => {
    if (error.response?.status === 401 && !error.config._retry && !error.config.url?.includes("/auth/")) {
      error.config._retry = true;
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        return axios(error.config);
      } catch {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (err) {
      // Fix EU (2026-10-01): sin conexión no se cierra la sesión; se vuelve a
      // intentar sola cuando vuelve la señal (la app instalada abre sin internet).
      if (!err?.response) {
        window.addEventListener("online", () => checkAuth(), { once: true });
        return;
      }
      // Fix EU: al abrir la app con la sesión vencida (8 h) se renueva con el
      // token de renovación, igual que ya pasa en cualquier otra pantalla.
      if (err.response.status === 401) {
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(data);
          return;
        } catch {
          /* sin renovación posible: va al login */
        }
      }
      setUser(null);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    // Fix HB (2026-10-20): el login devuelve `id` y /auth/me devuelve `_id`; sin `_id` hasta recargar, el botón
    // «Aprobar» del análisis no aparecía justo después de iniciar sesión.
    setUser({ ...data, _id: data._id || data.id });
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // ignore errors on logout
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { API };
